import { Fragment, ReactNode } from 'react';
import { Link } from 'react-router-dom';

/**
 * Breadcrumbs — current-location trail above page content.
 *
 * Derived from M3 (no standalone Breadcrumb component) per Agent
 * Protocol step 2:
 *   - Spec:      M3 Navigation guidance implies breadcrumbs via the
 *                Top App Bar headline + supporting text pattern; no
 *                explicit component. Composed pattern.
 *   - Reference: github.com/material-components/material-web (none)
 *
 * Populus adaptations applied:
 *   - Text + chevron separator (›) — matches business-app conventions
 *     (vs M3 implied dropdown menus for deep hierarchies)
 *   - Last segment in --color-ink + medium weight (current location;
 *     not a link)
 *   - Earlier segments in --color-ink-3 + link styling; hover brand
 *     accent on link items
 *   - First segment optional (e.g., "Clients › Alliance Property Tax
 *     › 758 Kirby Dr Retail" — three levels deep)
 *
 * Token discipline:
 *   - All text/separator → --color-ink* (system; never per-org)
 *   - Hover accent on links → --brand-primary (org-overridable per
 *     design-system.md § Color — System vs Brand tokens)
 *
 * Phase 11 Block 6 PR 4 (2026-05-12).
 */

export interface BreadcrumbItem {
  label: ReactNode;
  to?: string;        // undefined → current page (non-link)
}

export interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  /**
   * Compact/mobile mode: collapse the trail to just the current (leaf) page
   * so the top bar's action cluster (View-as / Sign out) stays reachable on a
   * ~390px viewport. The leaf label truncates with an ellipsis instead of
   * overflowing the bar (fixes the reported iPhone 13 Pro Max header overlap).
   */
  collapse?: boolean;
}

export function Breadcrumbs({ items, collapse = false }: BreadcrumbsProps) {
  // On phones, render only the leaf crumb (the current page) — earlier
  // ancestors are one tap away via the back button / drawer nav, and keeping
  // the full trail here is what pushed "Sign out" off the right edge.
  const rendered = collapse && items.length > 1 ? items.slice(-1) : items;
  return (
    <nav aria-label="Breadcrumb" style={{
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'nowrap',
      overflow: 'hidden',
      gap: 'var(--sp-2)',
      fontSize: 'var(--fs-sm)',
      color: 'var(--color-ink-3)',
    }}>
      {rendered.map((item, i) => {
        // Leaf of the rendered trail = current page (in collapse mode this is
        // the only crumb). It never links and it is the one that ellipsizes.
        const isLast = i === rendered.length - 1;
        const sep = (
          <span aria-hidden="true" style={{
            color: 'var(--color-muted)',
            margin: '0 var(--sp-1)',
            flexShrink: 0,
          }}>›</span>
        );
        const content = item.to && !isLast ? (
          <Link
            to={item.to}
            style={{
              color: 'var(--color-ink-3)',
              textDecoration: 'none',
              fontWeight: 'var(--fw-regular)',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {item.label}
          </Link>
        ) : (
          <span style={{
            color: isLast ? 'var(--color-ink)' : 'var(--color-ink-3)',
            fontWeight: isLast ? 'var(--fw-medium)' : 'var(--fw-regular)',
            // The current-page crumb absorbs the shrink and ellipsizes so the
            // trail can never overflow the top bar.
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minWidth: 0,
            flex: isLast ? '0 1 auto' : '0 0 auto',
          }} aria-current={isLast ? 'page' : undefined}>
            {item.label}
          </span>
        );
        return (
          <Fragment key={i}>
            {i > 0 && sep}
            {content}
          </Fragment>
        );
      })}
    </nav>
  );
}
